/**
 * Student sponsorship agreement sections, ported from the RN app
 * (`apps/uninfo/src/assets/studentSposnorshipAgreementDemo.ts`) so the web
 * redeem flow shows the same clauses the mobile app signs.
 *
 * `__TOKEN__` placeholders are resolved by `resolveAgreementSections`:
 *   __SPONSOR_SCOPE__     – sponsor name + description
 *   __SPONSOR_TASKS__     – the sponsor's task titles plus their obligations
 *   __TIMELY_COMPLETION__ – activity name/date + the one-month rule
 *
 * Mirrored byte-for-byte by the RN `buildAgreementHtml` in
 * `apps/uninfo/src/assets/studentSposnorshipAgreementDemo.ts`. The signed text is
 * hardcoded English in both apps and must stay identical: it is a legal
 * document, so a translation layer cannot be allowed to fork it.
 */

export type AgreementSection = {
  title: string;
  content: string | string[] | Record<string, string>;
  /**
   * The section's own number, counted over `AGREEMENT_SECTIONS` including the
   * front page. The body starts at 2 because "Front Page Info" is 1 and is
   * filtered out before the sections render, which is what lets the prose
   * cross-refer to "Section 8" and mean the clause it names.
   */
  number?: number;
};

export const AGREEMENT_SECTIONS: AgreementSection[] = [
  {
    title: "Front Page Info",
    content: {
      agreementDate: "",
      sponsorName: "",
      sponsorContactName: "",
      sponsorTel: "",
      sponsorEmail: "",
    },
  },
  {
    title: "Scope of sponsorship",
    content: "__SPONSOR_SCOPE__",
  },
  {
    title: "Sponsor-related Tasks and Obligations",
    content: "__SPONSOR_TASKS__",
  },
  {
    title: "Sponsor Delivery Method",
    content: [
      "Sponsor Provider will contact the student through the UninfoHK app before one month of the activity start date to arrange delivery method and details.",
      "If the student does not collect the sponsorship items, all promotional obligations must still be completed.",
    ],
  },
  {
    title: "Timely Completion",
    content: "__TIMELY_COMPLETION__",
  },
  {
    title: "Verification of Completion",
    content: [
      "Proof of completion for all sponsor-related tasks must be submitted to the Sponsor Provider within one month from the event’s start date by uploading through the Uninfo HK app.",
      "Proof may include screenshots or videos, clearly labeled with task, member name, role, and date.",
      "Upon receipt of sponsored items, the Society must upload a photo of the sponsored items from its official Instagram account to UninfoHK's official Instagram account (@uninfohk) on the same day.",
      "Sponsor Provider will review all proof and may request resubmission if insufficient.",
      "Both parties should retain copies of all submitted and reviewed proof.",
    ],
  },
  {
    title: "Confidentiality and Non-Disclosure",
    content: [
      "Both parties agree to maintain confidentiality of any proprietary or sensitive information shared.",
    ],
  },
  {
    title: "Compensation and Breach",
    content: [
      "Best Efforts: The Student agrees to make their best efforts to fulfill all the obligations outlined in this Agreement.",
      "Breach and Cure: In the event the Student fails to fulfill any of the requirements captured in this Agreement, the Sponsor Provider shall provide written notice detailing the unfulfilled requirement. A Cure Period shall only be granted if the Student has submitted proof of completion within the sponsor task period specified in Section 5. If no proof is submitted within the sponsor task period, the breach shall be deemed final, and the compensation provisions set forth in Section 8 shall apply immediately without any Cure Period.",
      "Continuing Obligations: Notwithstanding the delivery or non‑delivery of the sponsorship items, the Student shall remain fully responsible for complying with all obligations set forth in this Agreement, including but not limited to the promotional and application‑related tasks, and shall complete such tasks within the timelines specified herein.",
      "Compensation for Incomplete Tasks: If the Student fails to provide any of the following: Proof of completion for any sponsor-related task, then the Student must pay the Sponsor Provider HKD $3,500 as liquidated damages. For each uncompleted task: An additional 4% interest on the base compensation (HKD $3,500) will be charged once the breach is confirmed. After that, a daily interest of 2% on the outstanding amount will accrue until the task is completed or the full payment is made. All compensation must be paid into the Sponsor Provider’s designated bank account within seven (7) days of becoming due. If payment is not made within this period, the daily interest of 2% per uncompleted task will continue to accrue until the amount is settled in full.",
    ],
  },
  {
    title: "Governing Law and Dispute Resolution",
    content: [
      "This Agreement is governed by the laws of Hong Kong SAR.",
      "Disputes will be resolved first by negotiation, then by the courts of Hong Kong SAR if unresolved.",
    ],
  },
  {
    title: "Exclusive Use of Sponsor",
    content: [
      "Sponsorship must be used exclusively for club-related activities; personal use is prohibited.",
    ],
  },
  {
    title: "Signatures",
    content: [
      "Both parties hereby agree to the terms and conditions set forth in this Agreement.",
    ],
  },
  {
    title: "Right to Audit and Verification",
    content: [
      "Sponsor Provider may request additional documentation or perform spot checks to verify compliance.",
    ],
  },
  {
    title: "Use of Sponsor’s Name, Logo, and Branding",
    content: [
      "The Student may not use the Sponsor Provider’s name, logo, or branding without prior written consent, except as required by this Agreement.",
    ],
  },
  {
    title: "Termination for Cause",
    content: [
      "Sponsor Provider may terminate the Agreement immediately for material breach, false information, or conduct harming its reputation.",
    ],
  },
  {
    title: "Indemnification",
    content: [
      "The Student agrees to indemnify and hold harmless the Sponsor Provider from any claims or damages related to this Agreement.",
    ],
  },
  {
    title: "No Assignment",
    content: [
      "The Student may not assign or transfer rights or obligations without Sponsor Provider’s written consent.",
    ],
  },
  {
    title: "Force Majeure",
    content: [
      "Neither party is liable for failure to perform due to causes beyond reasonable control (force majeure).",
    ],
  },
  {
    title: "Entire Agreement and Amendments",
    content: [
      "This Agreement is the entire agreement; amendments must be in writing and signed by both parties.",
    ],
  },
];

/** "27 September 2026" — the front-page agreement date format. */
export const getAgreementDateString = (date = new Date()) => {
  const month = date.toLocaleString("en-US", { month: "long" });
  return `${date.getDate()} ${month} ${date.getFullYear()}`;
};

/**
 * The resolved requirement the API attaches to each task as `requirement`.
 *
 * The client never computes these numbers: `followerCount` depends on the
 * activity row and on the student-declared participant count, so only the server
 * can resolve it. Read-only here because the agreement restates an obligation,
 * it does not enforce one.
 */
export type ResolvedTaskRequirement = {
  /** The declared participant headcount, for a title carrying `{number}`. */
  participants?: number | null;
  followerCount?: number | null;
  followerCountCap?: number | null;
  followTargets?: string[] | null;
  usernameMin?: number | null;
  media?: { min?: number | null; max?: number | null; mime?: string[] | null } | null;
  accountsToRegister?: string[] | null;
};

export type AgreementTaskAvailabilityConflict = {
  handles: string[];
  sponsor_name?: string | null;
  sponsor_id?: string | null;
  activity_id?: string | null;
};

/** One task as the agreement needs it: the title plus its resolved obligation. */
export type AgreementSponsorTask = {
  id?: string;
  title: string;
  type?: string | null;
  requirement?: ResolvedTaskRequirement | null;
  available?: boolean;
  unavailable_reason?: string | null;
  unavailable_conflicts?: AgreementTaskAvailabilityConflict[];
};

/** Render an IG handle list for legal text, tolerating the stored `@` placeholder. */
const joinHandles = (handles?: string[] | null): string[] =>
  (Array.isArray(handles) ? handles : [])
    .map((handle) => String(handle || "").trim())
    .filter((handle) => handle && handle !== "@")
    .map((handle) => (handle.startsWith("@") ? handle : `@${handle}`));

/**
 * The `{number}` placeholder a task title may carry, and the count that fills it.
 *
 * A title can state its obligation with a placeholder when the number is owned by
 * the server and scales with the participant count, so the same stored title is
 * correct for every redemption. Substituting the resolved count is what makes one
 * title serve all of them.
 */
const NUMBER_PLACEHOLDER = /\{number\}/g;

/** Whether a title asks for a number, without the stateful `lastIndex` of a global regex. */
const hasNumberPlaceholder = (title: string): boolean =>
  /\{number\}/.test(title);

/**
 * Fill a title's `{number}` placeholder from its server-resolved requirement.
 *
 * The placeholder stands for the participant headcount, so it is filled with
 * `requirement.participants` — the count the student declared. It is deliberately
 * not filled with `followerCount`: that is the number of accounts each
 * participant must follow, it is floored and capped, and substituting it here
 * would print a participant count that was never declared (a capped 25 for an
 * activity of 1000).
 *
 * The placeholder is never left showing. When no count has been resolved the title
 * is returned without it, because a literal `{number}` in the middle of a sentence
 * is not something a student should ever read. Nothing is invented in its place,
 * since a number nobody declared would be worse than no number.
 *
 * Mirrored byte-for-byte by the RN `applyNumberPlaceholder`.
 */
const applyNumberPlaceholder = (task: AgreementSponsorTask): string => {
  const title = String(task?.title || "").trim();
  if (!hasNumberPlaceholder(title)) return title;
  // The number in the title is the follower requirement the student is held to, so
  // it is filled with `followerCount` -- the server-resolved, scaled count. 100
  // participants resolves to 25 accounts and the title states 25; 1000 resolves to
  // 250. Falling back to the declared headcount only covers a task that resolved no
  // follower count at all, so the sentence still reads with a real number.
  const followerCount = task?.requirement?.followerCount;
  const participants = task?.requirement?.participants;
  const resolved =
    typeof followerCount === "number"
      ? followerCount
      : typeof participants === "number"
        ? participants
        : null;
  if (resolved !== null) {
    return title.replace(NUMBER_PLACEHOLDER, String(resolved));
  }
  // Drop the placeholder, then repair the spacing and punctuation the removal
  // leaves behind, so the sentence still reads correctly.
  return title
    .replace(NUMBER_PLACEHOLDER, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+([,.])/g, "$1")
    .trim();
};


/**
 * One line per task: the task's own title and nothing else.
 *
 * The obligation sentences used to be appended here ("— Submit at least 1 video as
 * proof.", "— Register at least 1 account(s).", and for `ensure_followers` a follower
 * count and a restatement of the target handles). Every one of them restated
 * something the title already carried or that the server enforces anyway, and
 * appending them made each line a run-on of two unrelated sentences. The title is
 * the whole line now.
 *
 * The requirements are unchanged: `media.min`/`media.max`, `usernameMin` and
 * `followerCount` are still carried on `task.requirement` and still enforced by
 * `validateMediaSubmission` / `validateUsernameSubmission` /
 * `validateFollowerSubmission`. Removing the sentences drops them from the prose,
 * not from the contract the server checks.
 *
 * Titles go through `applyNumberPlaceholder`, so a title carrying `{number}` states
 * the count for the participant count being signed.
 */
export const describeSponsorTasks = (
  tasks: AgreementSponsorTask[],
): string[] =>
  (Array.isArray(tasks) ? tasks : [])
    .map((task) => applyNumberPlaceholder(task))
    .filter((line) => line.length > 0);

/**
 * The line the redeem flow's "Sponsor Tasks" list shows for one task.
 *
 * `ensure_followers` stores a `{number}` placeholder instead of a literal count,
 * because the count is owned by the server and scales with the participant count
 * the student is declaring. The placeholder is filled from the same
 * server-resolved requirement the signed agreement uses, so the list and the
 * contract cannot disagree about the number.
 *
 * Every other task's title is complete prose carrying no server-owned number, and
 * is rendered unchanged.
 *
 * Callers must pass the requirement the server resolved for the participant count
 * currently on screen, never a catalog copy: the catalog resolves with no
 * participants, so its `followerCount` is the formula's floor — a floor printed in
 * the list is a number the student is not held to. With no requirement the line
 * is the bare title, which is the honest state before the first response arrives
 * and after a failed one.
 *
 * Mirrored byte-for-byte by the RN `describeSponsorTaskLine`.
 */
export const describeSponsorTaskLine = (task: AgreementSponsorTask): string => {
  const title = applyNumberPlaceholder(task);
  if (typeof task?.requirement?.participants !== "number") return title;
  return describeSponsorTasks([task])[0] || title;
};

/**
 * Alphabet and length of the quote number's opaque segment. Crockford-ish (no
 * `I`, `O`, `0`, `1`) so a number read off a printed agreement can be typed back
 * without ambiguity.
 */
const QUOTE_TOKEN_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const QUOTE_TOKEN_LENGTH = 8;

/**
 * An unguessable token to stand in for what used to be two personal numbers.
 *
 * `crypto` where the host has it; `Math.random` otherwise, which is enough
 * because the number is already unique to the second the agreement was signed.
 */
const randomQuoteToken = (): string => {
  const bytes = new Uint8Array(QUOTE_TOKEN_LENGTH);
  const source = globalThis.crypto;
  if (source && typeof source.getRandomValues === "function") {
    source.getRandomValues(bytes);
  } else {
    for (let i = 0; i < QUOTE_TOKEN_LENGTH; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(
    bytes,
    (byte) => QUOTE_TOKEN_ALPHABET[byte % QUOTE_TOKEN_ALPHABET.length],
  ).join("");
};

/**
 * `SA260927142530-BC-7F3K9Q2M` — the front-page quote number, ported from the
 * RN `ModalAgreementSignature` so web and mobile quote references look alike.
 *
 * The middle segment used to be the student's phone last four and their HKID
 * last four. That whole string is the Cloudinary public_id of the finalized PDF,
 * which is served publicly and without authentication, so the reference a party
 * quotes on the phone was enough to hand a stranger eight digits of two identity
 * documents. The token carries no personal data and is not guessable, and the
 * date-and-time prefix still keeps the number unique per agreement.
 */
export const getAgreementQuoteNumber = ({
  hkidName,
  now = new Date(),
}: {
  hkidName?: string | null;
  now?: Date;
}): string => {
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const min = String(now.getMinutes()).padStart(2, "0");
  const ss = String(now.getSeconds()).padStart(2, "0");
  const shortName = hkidName
    ? hkidName
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
    : "XXX";
  return `SA${yy}${mm}${dd}${hh}${min}${ss}-${shortName}-${randomQuoteToken()}`;
};

/**
 * A student's HKID as it may appear on a document: the leading letter and the
 * last three digits, the middle replaced by asterisks.
 *
 * The full number is what the database holds and what an authorized reviewer
 * needs, but the agreement is uploaded to a public, unauthenticated URL and the
 * body text elsewhere refers to "as shown on HKID" without needing the whole
 * document number. Anything with three or fewer digits, or no digits at all, is
 * starred out entirely rather than passed through.
 */
export const maskHkid = (value?: string | null): string => {
  const hkid = String(value ?? "").trim();
  if (!hkid) return "";
  const letter = /^[A-Za-z]/.test(hkid) ? hkid.slice(0, 1).toUpperCase() : "";
  const digits = hkid.replace(/\D/g, "");
  if (digits.length <= 3) return `${letter}${"*".repeat(digits.length)}`;
  return `${letter}${"*".repeat(digits.length - 3)}${digits.slice(-3)}`;
};

/**
 * Whether a run of digits reads as filler rather than as a registry number.
 *
 * `00008888` and `11111111` are what a hand-typed placeholder looks like: one digit
 * repeated, or barely any digit varying at all. Registry numbers are issued in
 * sequence and a real one varies. Under-rejecting prints an identification the
 * company does not have, which is the outcome not worth the risk of over-rejecting
 * a real number for.
 */
const looksLikePlaceholderDigits = (digits: string): boolean =>
  new Set(digits.split("")).size < 3;

/**
 * Whether a provider BR number may be printed on a signed agreement.
 *
 * A Hong Kong company party has to be identified by name and BR number, but two
 * of the ways this column is filled are not BR numbers at all: `"LEGACY"` is
 * what an un-migrated business profile stores, and a run of filler digits is not
 * something the Companies Registry issued. Printing either would state an
 * identification that does not exist, so the row is dropped and the operator is
 * pointed at the column to fix. A real number is returned untouched by the caller.
 */
export const isUsableBrNumber = (value?: string | null): boolean => {
  const brNumber = String(value ?? "").trim();
  if (!brNumber) return false;
  if (brNumber.toUpperCase() === "LEGACY") return false;
  // A BR number is digits. Anything else — "TBC", a bare separator — is not an
  // identification whatever else it says about the company.
  const digits = brNumber.replace(/\D/g, "");
  if (!digits) return false;
  return !looksLikePlaceholderDigits(digits);
};

/**
 * The RN front page ("QUOTE" header + Sponsor Provider / Student columns).
 *
 * `sponsorName` is the product title, so it identifies nothing legal: the
 * provider's registered identity arrives separately as `providerLegalName`,
 * `sponsorBrNumber` and `sponsorAddress`, and `sponsorContactName` is that same
 * legal name because a business profile records a company rather than a named
 * contact. The BR number is already vetted by `isUsableBrNumber` before it gets
 * here, so its presence means it may be printed.
 */
export type AgreementFrontPageInfo = {
  quoteNumber: string;
  agreementDate: string;
  sponsorName?: string;
  sponsorContactName?: string;
  sponsorTel?: string;
  sponsorEmail?: string;
  providerLegalName?: string;
  /** Omitted when the stored BR number is missing or still a placeholder. */
  sponsorBrNumber?: string;
  sponsorAddress?: string;
  clubName?: string;
  clubRepName?: string;
  clubRepHkid?: string;
  clubTel?: string;
  clubEmail?: string;
  generatedTime: string;
};

export const resolveAgreementSections = ({
  sponsorName,
  sponsorDescription,
  sponsorTasks,
  activityName,
  activityDate,
}: {
  sponsorName?: string;
  sponsorDescription?: string;
  sponsorTasks?: AgreementSponsorTask[];
  activityName?: string;
  activityDate?: string;
}): AgreementSection[] =>
  AGREEMENT_SECTIONS
    // Numbered over the whole list, "Front Page Info" being section 1, and only
    // then filtered: numbering the body on its own would shift every section by
    // one and break the "Section 8" the breach clause points at.
    .map((section, index) => ({ section, number: index + 1 }))
    .filter(({ section }) => section.title !== "Front Page Info")
    .map(({ section, number }) => {
      let content: string | string[] | Record<string, string> = section.content;
      if (content === "__SPONSOR_SCOPE__") {
        content =
          sponsorName || sponsorDescription
            ? [
                sponsorName ? `Sponsor: ${sponsorName}` : "",
                sponsorDescription ? `Description: ${sponsorDescription}` : "",
              ].filter(Boolean)
            : ["No sponsor information provided."];
      } else if (content === "__SPONSOR_TASKS__") {
        const described = describeSponsorTasks(sponsorTasks || []);
        content =
          described.length > 0 ? described : ["No sponsor tasks provided."];
      } else if (content === "__TIMELY_COMPLETION__") {
        const subject = activityName ? `"${activityName}"` : "the activity";
        content = [
          `All sponsor-related tasks for ${subject} must be completed within one month from the activity start date${activityDate ? ` (${activityDate})` : ""}.`,
        ];
      }
      return { number, title: section.title, content };
    });
